export const dashboardDocumentation = `
## Overview

The dashboard serves as the main landing page for users, providing key metrics, assessment alerts, and interactive data visualizations. It displays essential patient statistics and actionable information for healthcare management.

## Dashboard Widgets

### Information Blocks (Metrics Cards)

| Widget | Data Source | Display Format | Icon | Description |
|--------|-------------|----------------|------|-------------|
| Number of Patients | \`metrics.numberOfPatients\` | Numeric count | Patient Icon | Total count of patients in the account |
| Female Percentage | \`metrics.femalePercentage\` | Percentage (%) | Gender Icon | Percentage of female patients (rounded) |
| Average Age | \`metrics.averageAge\` | Numeric value or "—" | Age Icon | Average age of all patients in the account |

**Widget Features**:
- **Animation**: Grow effect with 1500ms timeout on data load
- **Visual Design**: Gradient backgrounds with hover effects
- **Loading States**: Fade-in animations when data becomes available
- **Empty Data**: Shows "—" when no data is available

### Charts Section

**Available Chart Types**:
- **Pie Charts** - For categorical data distribution
- **Bar Charts** - For comparative analysis
- **Line Charts** - For trend visualization

**Chart Data Categories**:

| Chart Category | Default Type | Description |
|----------------|--------------|-------------|
| Age Range | Pie | Patient age distribution |
| General Health | Bar | Overall health status metrics |
| Allergic Rhinitis | Line | Allergic rhinitis trends |
| Asthma | Bar | Asthma-related data |
| Allergic Conjunctivitis | Pie | Eye allergy distribution |
| Allergic Urticaria | Bar | Skin allergy metrics |
| Symptom Severity SNOT22 | Bar | SNOT-22 symptom scores |
| Physical Activity | Bar | Patient activity levels |
| Fruit Vegetable Consumption | Bar | Dietary consumption data |
| Tobacco Use | Bar | Tobacco usage patterns |

**Chart Interactions**:
- **Chart Type Toggle**: Switch between Pie/Bar/Line views
- **Hover Effects**: Interactive chart element highlighting
- **Preferences Storage**: Chart type selections saved to localStorage
- **Responsive Sizing**: Charts adjust to container width automatically

### Assessment Alerts Table

**Table Columns**:

| Column | Width | Description | Interactive |
|--------|-------|-------------|-------------|
| MRN Number | 200px | Medical Record Number | <span class="checkmark">✓</span> (Info pane) |
| Patient First Name | 240px | Patient's first name | <span class="checkmark">✓</span> (Info pane) |
| Patient Last Name | 240px | Patient's last name | <span class="checkmark">✓</span> (Info pane) |
| Assessment | 240px | Assessment name/type | <span class="crossmark">✗</span> |
| Completed Timestamp | 200px | Assessment completion date | <span class="crossmark">✗</span> |

**Table Features**:
- **Row Click Navigation**: Navigates to assessment report page
- **Info Pane Integration**: Click patient data to view details
- **Loading States**: Spinner during data fetch
- **Empty State Handling**: Graceful handling of no data

## User Actions

| Action | Location | Permission Required | Description |
|--------|----------|-------------------|-------------|
| Add Patient | Dashboard header (top-right) | PATIENT_CREATE | Opens patient creation modal |
| View Assessment Report | Assessment alerts table row | ASSESSMENT_GET | Navigates to detailed assessment report |
| Toggle Chart Type | Charts section | DASHBOARD_GET | Switches between Pie/Bar/Line chart views |
| Open Info Pane | Patient data cells | DASHBOARD_GET | Shows detailed patient information |

## Permissions

### Required for Dashboard Access
- **DASHBOARD_GET**: Basic permission to view dashboard (BigInt bit 15)

### Feature-Specific Permissions
- **PATIENT_CREATE**: Required for "Add Patient" button functionality
- **ASSESSMENT_GET**: Required for accessing assessment reports
- **ACCOUNTS_GET (for DS account)**: May provide additional cross-account dashboard features

### Permission Behavior
- **Missing DASHBOARD_GET**: User redirected to allowed route or access denied
- **Missing Feature Permissions**: Specific buttons/actions become unavailable
- **Feature Toggle**: Dashboard can be disabled via \`disabledFeatures\` configuration

## Account Type Dependencies

### Distribution Sponsor (DS) Accounts
- **Full dashboard access** with enhanced administrative capabilities
- Cross-account metrics aggregation across child accounts
- Enhanced reporting capabilities for multiple account management
- Additional permissions for child account management
- **Note**: DS accounts are "super accounts" designed to work through the dashboard interface

### Regular Accounts
- Standard dashboard with account-specific metrics
- Single-account data visualization
- All standard dashboard features available

## API Integration

### Primary Endpoint
**GET** \`/accounts/{accountUuid}/metrics\`

### Response Structure
\`\`\`json
{
  "metrics": {
    "numberOfPatients": 1234,
    "femalePercentage": 0.52,
    "averageAge": 45.7,
    "chartData": {
      "assessmentAlerts": [...],
      "ageRange": [...],
      "generalHealth": [...],
      "allergicRhinitis": [...],
      "asthma": [...],
      "allergicConjunctivitis": [...],
      "allergicUrticara": [...],
      "symptomSeveritySnot22": [...],
      "physicalActivity": [...],
      "fruitVegetableConsumption": [...],
      "tobaccoUse": [...]
    }
  }
}
\`\`\`

### Loading States
- **\`REQUEST_STATUS.idle\`**: Initial dashboard state
- **\`REQUEST_STATUS.loading\`**: Data fetching in progress
- **\`REQUEST_STATUS.succeeded\`**: Data loaded successfully
- **\`REQUEST_STATUS.failed\`**: Error state with fallback UI

## Navigation and Routing

### Dashboard Route
- **Path**: \`/:currentAccountUuid/dashboard\`
- **Default Landing**: Primary page after login
- **Breadcrumb**: Home > Dashboard

### Navigation Actions
- **Assessment Reports**: \`/assessment/report\` with parameters:
  - \`currentAccountUuid\`
  - \`patientUuid\`
  - \`assessmentUuid\`
  - \`status: 'pendingReview'\`

## Error Handling and States

### Error States
- **Data Loading Errors**: Shows "Error loading data" message with empty state icon
- **Network Failures**: Displays error UI with retry options
- **Permission Errors**: Redirects to appropriate allowed page

### Empty Data Handling
- **No Patients**: Metrics show "—" or zero values
- **No Assessments**: Empty table with placeholder message
- **No Chart Data**: Charts display empty state indicators

### Loading States
- **Skeleton Loading**: For charts during initial load
- **Spinner Indicators**: For table data fetching
- **Fade-in Animations**: For metrics cards when data loads

## Dashboard Personalization

### Persistent Settings
- **Chart Type Preferences**: Stored in localStorage as 'charts'
- **Layout Preferences**: Sidebar collapse state
- **Theme Settings**: Material-UI theme consistency

### Responsive Design
- **Grid System**: Adapts to different screen sizes
- **Chart Sizing**: Dynamic width adjustment based on container
- **Mobile Optimization**: Touch-friendly interactions and layouts

## Testing

For comprehensive testing scenarios and interactive checklists for the dashboard functionality, please refer to the **Testing Scenarios** section in the documentation navigation.

## Performance Considerations

### Data Loading
- **Metrics calculated server-side** for optimal performance
- **Chart data optimized** for visualization libraries
- **Assessment alerts paginated** for large datasets

### Chart Rendering
- **ECharts library** provides performant visualizations
- **Lazy loading** for chart components
- **Memory management** for chart instances

### Caching Strategy
- **Chart preferences** cached in localStorage
- **Dashboard metrics** may be cached based on data freshness requirements
- **Assessment alerts** refreshed on dashboard load
`;
